import { Component, Input, OnInit, output, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';

export type QuickDatePreset = '7d' | '30d' | '3m' | '6m' | '12m' | 'custom';

export interface QuickDateRange {
  dateFrom: string;
  dateTo: string;
}

export interface PresetConfig {
  key: QuickDatePreset;
  labelKey: string;
  days: number | null;
}

export const QUICK_DATE_PRESETS: PresetConfig[] = [
  { key: '7d', labelKey: 'ADMIN.quickDatePicker.preset.7d', days: 7 },
  { key: '30d', labelKey: 'ADMIN.quickDatePicker.preset.30d', days: 30 },
  { key: '3m', labelKey: 'ADMIN.quickDatePicker.preset.3m', days: 90 },
  { key: '6m', labelKey: 'ADMIN.quickDatePicker.preset.6m', days: 180 },
  { key: '12m', labelKey: 'ADMIN.quickDatePicker.preset.12m', days: 365 },
  { key: 'custom', labelKey: 'ADMIN.quickDatePicker.preset.custom', days: null },
];

export function computeQuickDateRange(
  preset: QuickDatePreset,
  customFrom?: Date | null,
  customTo?: Date | null,
): QuickDateRange | null {
  if (preset === 'custom') {
    if (!customFrom || !customTo) return null;
    return { dateFrom: fmtDate(customFrom, false), dateTo: fmtDate(customTo, true) };
  }
  const cfg = QUICK_DATE_PRESETS.find((p) => p.key === preset);
  if (!cfg?.days) return null;
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - cfg.days);
  return { dateFrom: fmtDate(from, false), dateTo: fmtDate(to, true) };
}

function fmtDate(d: Date, isTo: boolean): string {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}T${isTo ? '23:59:59' : '00:00:00'}`;
}

@Component({
  selector: 'app-quick-date-picker',
  standalone: true,
  imports: [
    FormsModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    TranslateModule,
  ],
  templateUrl: './quick-date-picker.component.html',
  styleUrls: ['./quick-date-picker.component.scss'],
})
export class QuickDatePickerComponent implements OnInit {
  @Input() storageKey = 'quick-date-picker-preset';
  @Input() defaultPreset: QuickDatePreset = '30d';

  readonly dateRangeChange = output<QuickDateRange>();

  readonly presets = QUICK_DATE_PRESETS;
  readonly selectedPreset = signal<QuickDatePreset>('30d');
  readonly customFrom = signal<Date | null>(null);
  readonly customTo = signal<Date | null>(null);

  ngOnInit(): void {
    const saved = localStorage.getItem(this.storageKey) as QuickDatePreset | null;
    this.selectedPreset.set(saved ?? this.defaultPreset);
  }

  selectPreset(key: QuickDatePreset): void {
    this.selectedPreset.set(key);
    localStorage.setItem(this.storageKey, key);
    if (key !== 'custom') {
      this.emitRange();
    }
  }

  onCustomFromChange(value: Date | null): void {
    this.customFrom.set(value);
    if (value && this.customTo()) this.emitRange();
  }

  onCustomToChange(value: Date | null): void {
    this.customTo.set(value);
    if (this.customFrom() && value) this.emitRange();
  }

  private emitRange(): void {
    const range = computeQuickDateRange(this.selectedPreset(), this.customFrom(), this.customTo());
    if (range) this.dateRangeChange.emit(range);
  }
}
